import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useConnect } from '@lore/query-connect';
import { PayloadStates } from '@lore/utils';
import { useUser } from '@lore/auth';
import ClampableText from '@/components/ClampableText';
import Avatar from '../../components/Avatar';
import logo from '@communityshare/ui-shared/images/logo.png';
import { getDistanceFromLatLonInMiles } from '@/components/User/utils';
import UserOrganization from '@/pages/people/__common/UserOrganization.v2';
import useTags from '@/hooks/useTags';
import TagTypes from '@/constants/TagTypes';
import useProjectTags from '@/hooks/useProjectTags';
import { formatDate, formatDateRange, formatTimeRange } from '../dateTimeUtils';

useProjectMarkdown.propTypes = {
  project: PropTypes.object.isRequired
};

export default function useProjectMarkdown(project, partner, customMessage) {
  // const { project } = props;

  const currentUser = useUser();
  const distance = getDistanceFromLatLonInMiles(partner.data, currentUser.data);

  const activity = useConnect('tag.byId', {
    id: project.data.activityId
  });

  const days = useTags(TagTypes.Day);
  const projectDays = useProjectTags(project, TagTypes.Day);

  const times = useTags(TagTypes.Time);
  const projectTimes = useProjectTags(project, TagTypes.Time);

  const commitments = useTags(TagTypes.Commitment);
  const projectCommitments = useProjectTags(project, TagTypes.Commitment);

  const projectInteractionTypes = useProjectTags(project, TagTypes.InteractionType);

  if (
    activity.state === PayloadStates.FETCHING ||
    days.state === PayloadStates.FETCHING ||
    projectDays.state === PayloadStates.FETCHING ||
    times.state === PayloadStates.FETCHING ||
    projectTimes.state === PayloadStates.FETCHING ||
    commitments.state === PayloadStates.FETCHING ||
    projectCommitments.state === PayloadStates.FETCHING ||
    projectInteractionTypes.state === PayloadStates.FETCHING
  ) {
    return '...';
  }

  function getActivity() {
    return {
      title: `${activity.data.name}`,
      text: `${activity.data.description}`
    };
  }

  function getSummary() {
    return {
      title: `${project.data.title || activity.data.name}`,
      text: `${project.data.interactionDescription || activity.data.description}`
    };
  }


  function getDate() {
    if (
      project.data.specificDate === 'Yes' &&
      project.data.date
    ) {
      return {
        title: `Date`,
        text: formatDate(project.data.date)
      };
    }

    if (
      project.data.specificDate === 'No' &&
      project.data.startDate &&
      project.data.endDate
    ) {
      return {
        title: `Date Window`,
        text: formatDateRange(project.data.startDate, project.data.endDate)
      };
    }

    return {
      title: `Date`,
      text: `(not provided)`
    };
  }


  function getTime() {
    if (
      project.data.specificTime === 'Yes' &&
      project.data.startTime &&
      project.data.endTime
    ) {
      return {
        title: `Time`,
        text: formatTimeRange(project.data.startTime, project.data.endTime, currentUser.data.timezone)
      };
    }

    if (projectTimes.data.length > 0) {
      return {
        title: `Possible Times`,
        text: _.sortBy(projectTimes.data, m => m.data.timeId).map(function(projectTime) {
          return projectTime.data.name;
        }).join('\n')
      };
    }

    return {
      title: `Time`,
      text: `(not provided)`
    };
  }


  function getInPerson() {
    return !!_.find(projectInteractionTypes.data, function(projectInteractionType) {
      return projectInteractionType.data.name === 'In-person';
    });
  }

  function getVirtual() {
    return !!_.find(projectInteractionTypes.data, function(projectInteractionType) {
      return projectInteractionType.data.name === 'Virtual';
    });
  }

  const data = {
    activity: getActivity(),
    summary: getSummary(),
    // outcome: getOutcome(),
    date: getDate(),
    // days: getDays(),
    time: getTime(),
    // commitment: getCommitment(),
    inPerson: getInPerson(),
    virtual: getVirtual()
  }

  return (
    <div className="bg-cs-gray-50 p-6">
      <div className="h-1 bg-cs-orange-500 w-full mb-8"/>
      <div className="p-6 mb-8">
        <img
          className="w-24 mx-auto"
          src={logo}
          alt="logo"
        />
      </div>
      <div className="mb-8">
        <div className="text-base text-cs-gray-900">
          Hi {partner.data.firstName},
        </div>
        <div className="mt-3 text-base text-cs-gray-900">
          <ClampableText markup>
            {customMessage}
          </ClampableText>
        </div>
        <div className="mt-3 mb-3">
          <div className="text-base text-cs-gray-900">
            Cheers,
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div>
            <Avatar size={12} src={currentUser.data.avatarUrl} />
          </div>
          <div>
            <div className="text-lg text-cs-gray-900 font-semibold">
              {currentUser.data.firstName} {currentUser.data.lastName}
            </div>
            <div className="text-sm text-cs-gray-500">
              <UserOrganization user={currentUser} />
            </div>
          </div>
        </div>
      </div>
      <div className="mb-8">
        <div className="flex flex-col text-center justify-center bg-cs-orange-500 rounded-xl p-6">
          <div className="text-2xl text-white font-semibold mb-2">
            Are you interested?
          </div>
          <div className="text-base text-white mb-4">
            That&apos;s wonderful! Connect with {currentUser.data.firstName} to participate, ask
            questions, offer feedback, or just to refer someone you think might be a good fit.
          </div>
          <div className="bg-white rounded-lg w-56 text-center py-3 m-auto">
            <div className="text-base text-cs-orange-500 uppercase font-semibold">
              Respond or Decline
            </div>
          </div>
        </div>
      </div>
      <div className="mb-8 space-y-4">
        <div className="text-2xl font-semibold">
          Details
        </div>
        <div className="bg-white rounded-lg p-6 space-y-8">
          <div className="text-2xl font-semibold">
            {data.summary.title}
          </div>
          <div>
            <div className="text-xl font-semibold text-cs-gray-900">
              Summary
            </div>
            <div className="text-base text-cs-gray-500">
              <ClampableText markup>
                {data.summary.text}
              </ClampableText>
            </div>
          </div>
          <div>
            <div className="text-xl font-semibold text-cs-gray-900">
              {data.date.title}
            </div>
            <div className="text-cs-gray-500">
              <ClampableText markup>
                {data.date.text}
              </ClampableText>
            </div>
          </div>
          {/*<div>*/}
          {/*  <div className="text-xl font-semibold text-cs-gray-900">*/}
          {/*    {data.time.title}*/}
          {/*  </div>*/}
          {/*  <div className="text-cs-gray-500">*/}
          {/*    <ClampableText markup>*/}
          {/*      {data.time.text}*/}
          {/*    </ClampableText>*/}
          {/*  </div>*/}
          {/*</div>*/}
          <div>
            <div className="text-xl font-semibold text-cs-gray-900">
              Collaboration Options
            </div>
            {!data.virtual && !data.inPerson && (
              <div>
                <div className="text-cs-gray-900">
                  (not provided)
                </div>
                <div className="text-xs text-cs-gray-500">
                  {distance <= 1 ? '1 mile away' : `${distance} miles away`}
                </div>
              </div>
            )}
            {data.virtual && (
              <div>
                <div className="text-cs-gray-900 mb-1">
                  Virtual
                </div>
              </div>
            )}
            {data.inPerson && (
              <div>
                <div className="text-cs-gray-900">
                  In-person
                </div>
                <div className="text-xs text-cs-gray-500">
                  {distance <= 1 ? '1 mile away' : `${distance} miles away`}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="text-2xl font-semibold">
          Activity
        </div>
        <div className="bg-white rounded-lg p-6">
          <div className="text-xl font-semibold text-cs-gray-900">
            {data.activity.title}
          </div>
          <div className="text-cs-gray-500">
            {data.activity.text}
          </div>
        </div>
      </div>
      <div className="mb-8">
        <div className="flex flex-col text-center justify-center bg-cs-orange-500 rounded-xl p-6">
          <div className="text-2xl text-white font-semibold mb-2">
            Are you interested?
          </div>
          <div className="text-base text-white mb-4">
            That&apos;s wonderful! Connect with {currentUser.data.firstName} to participate, ask
            questions, offer feedback, or just to refer someone you think might be a good fit.
          </div>
          <div className="bg-white rounded-lg w-56 text-center py-3 m-auto">
            <div className="text-base text-cs-orange-500 uppercase font-semibold">
              Respond or Decline
            </div>
          </div>
        </div>
      </div>
      <div className="h-1 bg-cs-orange-500 w-full"/>
    </div>
  );
};
